/* =====================================================================
 * DMUserChatThread — 팀 멤버 간 대화 스레드
 *
 * 공용 MessageBubble을 재사용해 파일/이미지 카드 렌더링을
 * 에이전트 페이지와 동일하게 맞춥니다.
 * ===================================================================== */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import {
  ArrowUp,
  ChevronUp,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  LogOut,
  Plus,
  X,
} from 'lucide-react';
import { EmptyState } from '@auw/workspace-ui';
import { DropOverlay } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { Button } from '@auw/workspace-ui';
import { MessageBubble } from '../chat/bubble/MessageBubble';
import DMMentionNotice, { parseMentionEvent } from './DMMentionNotice';
import {
  buildAttachmentCountLimitToast,
  buildAttachmentSizeLimitToast,
  buildAttachmentTypeLimitToast,
  validateAttachmentBatch,
} from '../chat/attachmentConstraints';
import {
  addConversationMember,
  getConversation,
  sendConversationMessage,
  uploadConversationFiles,
} from '@auw/api-client';
import { useAutoScroll } from '@auw/workspace-utils';
import { useDMConversationStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { parseAttachmentFilesFromMeta } from '@auw/stores';
import { emitAppEvent } from '@auw/stores';
import { useFeatureFlags } from '@auw/feature-flags';
import type { ChatMessage, ConversationSharedGroup, FileAttachment } from '@auw/workspace-utils';
import type { Conversation, UserMessage } from '@auw/workspace-utils/types/dm';
import { extractClipboardFiles } from '@auw/workspace-utils';
import i18n from '@auw/i18n';

interface Props {
  conversation: Conversation;
  memberNames: Record<string, string>;
  memberPictures: Record<string, string | null>;
  transparentInput?: boolean;
  enableDropZone?: boolean;
}

interface PendingFileTypeInfo {
  icon: React.ReactNode;
  color: string;
  bg: string;
  ext: string;
}

function makeLocalAttachment(file: File): FileAttachment {
  const localId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  return {
    id: localId,
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: file.size,
    previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    file,
  };
}

function revokeAttachmentPreviews(attachments: FileAttachment[]): void {
  attachments.forEach((attachment) => {
    if (attachment.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getPendingFileTypeInfo(name: string, type: string): PendingFileTypeInfo {
  const ext = name.split('.').pop()?.toLowerCase() || '';

  if (type.startsWith('image/')) {
    return {
      icon: <ImageIcon size={16} />,
      color: '#8B5CF6',
      bg: 'rgba(139,92,246,0.1)',
      ext: ext.toUpperCase() || 'IMG',
    };
  }

  switch (ext) {
    case 'pdf':
      return { icon: <FileText size={16} />, color: '#EF4444', bg: 'rgba(239,68,68,0.1)', ext: 'PDF' };
    case 'xls':
    case 'xlsx':
    case 'xlsm':
    case 'csv':
      return {
        icon: <FileSpreadsheet size={16} />,
        color: '#22C55E',
        bg: 'rgba(34,197,94,0.1)',
        ext: ext.toUpperCase() || 'CSV',
      };
    case 'doc':
    case 'docx':
    case 'ppt':
    case 'pptx':
    case 'txt':
    case 'md':
      return {
        icon: <FileText size={16} />,
        color: '#3B82F6',
        bg: 'rgba(59,130,246,0.1)',
        ext: ext.toUpperCase() || 'DOC',
      };
    default:
      return {
        icon: <FileIcon size={16} />,
        color: '#6B7280',
        bg: 'rgba(107,114,128,0.1)',
        ext: ext.toUpperCase() || 'FILE',
      };
  }
}

function toChatMessage(
  message: UserMessage,
  memberNames: Record<string, string>,
  currentUserId: string,
): ChatMessage {
  const hasSystemEvent = typeof message.metadata?.system_event === 'string';
  return {
    id: message.message_id,
    role: message.content_type === 'system' || hasSystemEvent ? 'system' : 'user',
    content: message.content,
    contentType: message.content_type,
    metadata: message.metadata,
    attachments: parseAttachmentFilesFromMeta(message.metadata),
    createdAt: new Date(message.created_at).getTime(),
    isStreaming: false,
    streamEvents: [],
    senderId: message.sender_id,
    senderName:
      memberNames[message.sender_id]
      || (message.sender_id === currentUserId ? i18n.t('common:dm.me') : null),
    reactions: message.reactions || [],
  };
}

const DMUserChatThread: React.FC<Props> = ({
  conversation,
  memberNames,
  memberPictures,
  transparentInput = false,
  enableDropZone = false,
}) => {
  const { t } = useTranslation('common');
  const {
    messages,
    loadMessages,
    markRead,
    typingUsers,
    toggleReaction,
    updateConversationMeta,
    leaveConversation,
  } = useDMConversationStore();
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');
  const addToast = useChatStore((s) => s.addToast);
  const teams = useTeamStore((s) => s.teams);
  /* P11-5h: tenants without `mode.message.fileAttach` strip the entire
   * attachment surface (drop zone + paperclip + paste handler). The
   * server still rejects file uploads to be safe. */
  const fileAttachEnabled = useFeatureFlags().isEnabled('mode.message.fileAttach');
  const effectiveDropZone = enableDropZone && fileAttachEnabled;
  const convId = conversation.conversation_id;
  const convMessages = messages[convId] || [];
  const typingSet = typingUsers[convId];
  const typingList = typingSet ? Array.from(typingSet).filter((uid) => uid !== currentUserId) : [];
  const lastMessage = convMessages[convMessages.length - 1] ?? null;
  const autoScrollDeps = [
    lastMessage?.message_id ?? 'empty',
    lastMessage?.content.length ?? 0,
    typingList.length,
  ];
  const { containerRef: scrollRef, handleScroll, scrollToBottom } = useAutoScroll(
    autoScrollDeps,
    typingList.length > 0,
    convId,
  );

  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileAttachment[]>([]);
  const [headerAvatarError, setHeaderAvatarError] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [reinvitingMessageId, setReinvitingMessageId] = useState<string | null>(null);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [leavingConversation, setLeavingConversation] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFilesRef = useRef<FileAttachment[]>([]);
  const shouldRestoreFocusRef = useRef(false);
  const dragCounter = useRef(0);

  const restoreTextFocus = useCallback(() => {
    const target = textRef.current;
    if (!target) return;

    const applyFocus = () => {
      if (!target.isConnected) return false;
      if (document.activeElement !== target) {
        target.focus({ preventScroll: true });
      }
      const caret = target.value.length;
      try {
        target.setSelectionRange(caret, caret);
      } catch {
        // noop
      }
      return document.activeElement === target;
    };

    if (applyFocus()) return;
    window.requestAnimationFrame(() => {
      if (applyFocus()) return;
      window.setTimeout(() => {
        applyFocus();
      }, 24);
    });
  }, []);

  const hasMemberSnapshot = Array.isArray(conversation.members) && conversation.members.length > 0;
  const remainingMemberIds = useMemo(() => {
    const memberIds = new Set<string>();

    if (hasMemberSnapshot && conversation.members) {
      conversation.members.forEach((member) => {
        if (member.user_id) memberIds.add(member.user_id);
      });
    }

    if (currentUserId) {
      memberIds.add(currentUserId);
    }

    convMessages.forEach((message) => {
      const systemEvent = message.metadata?.system_event;
      const leftUserId = message.metadata?.left_user_id;
      const invitedUserId = message.metadata?.invited_user_id;
      if (systemEvent === 'member_left' && typeof leftUserId === 'string' && leftUserId.trim()) {
        memberIds.delete(leftUserId);
      }
      if (systemEvent === 'member_reinvited' && typeof invitedUserId === 'string' && invitedUserId.trim()) {
        memberIds.add(invitedUserId);
      }
    });

    return memberIds;
  }, [convMessages, conversation.members, currentUserId, hasMemberSnapshot]);

  const isSoloConversation = hasMemberSnapshot && remainingMemberIds.size <= 1;
  const currentMemberRole = useMemo(
    () => conversation.members?.find((member) => member.user_id === currentUserId)?.role ?? null,
    [conversation.members, currentUserId],
  );
  const canReinviteMembers = conversation.type === 'direct' || currentMemberRole === 'admin';
  const reinviteableMessageIds = useMemo(() => {
    const latestEventByUser = new Map<string, { messageId: string; event: 'left' | 'reinvited' }>();

    convMessages.forEach((message) => {
      const systemEvent = message.metadata?.system_event;
      const leftUserId = message.metadata?.left_user_id;
      const invitedUserId = message.metadata?.invited_user_id;

      if (systemEvent === 'member_left' && typeof leftUserId === 'string' && leftUserId.trim()) {
        latestEventByUser.set(leftUserId, { messageId: message.message_id, event: 'left' });
      }

      if (systemEvent === 'member_reinvited' && typeof invitedUserId === 'string' && invitedUserId.trim()) {
        latestEventByUser.set(invitedUserId, { messageId: message.message_id, event: 'reinvited' });
      }
    });

    const eligible = new Set<string>();
    latestEventByUser.forEach((value, userId) => {
      if (value.event === 'left' && !remainingMemberIds.has(userId)) {
        eligible.add(value.messageId);
      }
    });
    return eligible;
  }, [convMessages, remainingMemberIds]);

  useEffect(() => {
    pendingFilesRef.current = pendingFiles;
  }, [pendingFiles]);

  useEffect(() => {
    return () => {
      revokeAttachmentPreviews(pendingFilesRef.current);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    loadMessages(convId).then((more) => {
      setHasMore(more);
      setLoading(false);
    });
    markRead(convId);
  }, [convId, loadMessages, markRead]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        markRead(convId);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [convId, markRead]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = '0';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  useLayoutEffect(() => {
    if (!shouldRestoreFocusRef.current) return;
    shouldRestoreFocusRef.current = false;
    restoreTextFocus();
  }, [pendingFiles.length, restoreTextFocus, text, isSubmitting]);

  const handleLoadMore = useCallback(async () => {
    if (convMessages.length === 0) return;
    setLoading(true);
    const first = convMessages[0];
    const more = await loadMessages(convId, first.message_id);
    setHasMore(more);
    setLoading(false);
  }, [convId, convMessages, loadMessages]);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles((prev) => {
      revokeAttachmentPreviews(prev);
      return [];
    });
  }, []);

  const handleLeaveConversation = useCallback(() => {
    setLeaveConfirmOpen(true);
  }, []);

  const confirmLeaveConversation = useCallback(async () => {
    if (leavingConversation) return;
    setLeavingConversation(true);
    const left = await leaveConversation(convId);
    setLeavingConversation(false);
    if (!left) {
      addToast({ type: 'error', title: t('dm.leaveConversationFailed') });
      return;
    }
    setLeaveConfirmOpen(false);
    addToast({ type: 'info', title: t('dm.leftConversation') });
  }, [addToast, convId, leaveConversation, leavingConversation]);

  const handleReinviteMember = useCallback(async (message: ChatMessage) => {
    if (reinvitingMessageId) return;

    const leftUserId = typeof message.metadata?.left_user_id === 'string'
      ? message.metadata.left_user_id.trim()
      : '';

    if (!leftUserId) return;

    setReinvitingMessageId(message.id);
    try {
      await addConversationMember(convId, leftUserId, 'member_reinvited');
      const refreshedConversation = await getConversation(convId);
      updateConversationMeta(convId, refreshedConversation);
    } catch (err) {
      const description = err instanceof Error ? err.message : t('dm.reinviteFailed');
      if (description.includes('탈퇴한 회원입니다')) {
        addToast({ type: 'warning', title: t('dm.deactivatedMember') });
        return;
      }
      addToast({
        type: 'error',
        title: t('dm.reinviteError'),
        description,
      });
    } finally {
      setReinvitingMessageId(null);
    }
  }, [addToast, convId, reinvitingMessageId, updateConversationMeta]);

  const handleAddFiles = useCallback((fileList: FileList | File[] | null) => {
    if (!fileList) return;
    if (isSoloConversation) {
      addToast({
        type: 'warning',
        title: t('dm.cannotAttachFile'),
        description: t('dm.noMembersToSendFile'),
      });
      return;
    }
    const incoming = Array.isArray(fileList) ? fileList : Array.from(fileList);
    const { accepted, rejectedBySize, rejectedByCount, rejectedByType } = validateAttachmentBatch(
      incoming,
      pendingFilesRef.current.length,
    );

    if (rejectedByType.length > 0) {
      const toast = buildAttachmentTypeLimitToast(rejectedByType);
      addToast({ type: 'warning', title: toast.title, description: toast.description });
    }
    if (rejectedBySize.length > 0) {
      const toast = buildAttachmentSizeLimitToast(rejectedBySize);
      addToast({ type: 'warning', title: toast.title, description: toast.description });
    }
    if (rejectedByCount.length > 0) {
      const toast = buildAttachmentCountLimitToast(rejectedByCount.length);
      addToast({ type: 'warning', title: toast.title, description: toast.description });
    }
    if (accepted.length === 0) return;

    const nextAttachments = accepted.map(makeLocalAttachment);
    setPendingFiles((prev) => [...prev, ...nextAttachments]);
  }, [addToast, isSoloConversation]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleAddFiles(e.target.files);
    e.currentTarget.value = '';
  }, [handleAddFiles]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!fileAttachEnabled) return;
    const clipboardFiles = extractClipboardFiles(e.clipboardData);
    if (clipboardFiles.length === 0) return;
    e.preventDefault();
    handleAddFiles(clipboardFiles);
  }, [handleAddFiles, fileAttachEnabled]);

  const handleRemovePendingFile = useCallback((fileId: string) => {
    setPendingFiles((prev) => {
      const removed = prev.find((file) => file.id === fileId);
      if (removed?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return prev.filter((file) => file.id !== fileId);
    });
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (isSoloConversation) {
      addToast({
        type: 'warning',
        title: t('dm.cannotSendMessage'),
        description: t('dm.noMembersToSend'),
      });
      return;
    }
    if (isSubmitting || (!trimmed && pendingFiles.length === 0)) return;

    setIsSubmitting(true);
    try {
      let attachmentFiles: Array<{
        file_id: string;
        name?: string;
        mime_type?: string;
        size?: number;
      }> = [];

      if (pendingFiles.length > 0) {
        const filesToUpload = pendingFiles
          .map((attachment) => attachment.file)
          .filter((file): file is File => !!file);
        const uploadResult = await uploadConversationFiles(convId, filesToUpload);
        if (uploadResult.errors && uploadResult.errors.length > 0) {
          const failedNames = uploadResult.errors.map((entry) => entry.file).join(', ');
          addToast({
            type: 'warning',
            title: t('dm.someUploadsFailedTitle'),
            description: failedNames,
          });
        }
        attachmentFiles = uploadResult.uploaded_files.map((file) => ({
          file_id: file.file_id,
          name: file.name,
          mime_type: file.mime_type,
          size: file.size,
        }));
        if (uploadResult.uploaded_files.length > 0) {
          emitAppEvent('storage:agentFilesCreated');
        }
      }

      if (!trimmed && attachmentFiles.length === 0) {
        addToast({
          type: 'error',
          title: t('dm.noUploadedFiles'),
          description: t('dm.reselectFiles'),
        });
        return;
      }

      const metadata = attachmentFiles.length > 0
        ? { attachment_files: attachmentFiles }
        : {};
      const contentType = attachmentFiles.length > 0 && !trimmed ? 'file' : 'text';
      const optimisticMsg: UserMessage = {
        message_id: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        conversation_id: convId,
        sender_id: currentUserId,
        content: trimmed,
        content_type: contentType,
        metadata,
        created_at: new Date().toISOString(),
        edited_at: null,
        reactions: [],
      };

      scrollToBottom(false);
      useDMConversationStore.getState().addMessage(convId, optimisticMsg);
      setText('');
      clearPendingFiles();
      shouldRestoreFocusRef.current = true;
      restoreTextFocus();

      await sendConversationMessage(convId, trimmed, contentType, metadata);
    } catch (err) {
      const description = err instanceof Error ? err.message : t('dm.messageSendFailedDesc');
      addToast({
        type: 'error',
        title: t('dm.messageSendFailed'),
        description,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [addToast, clearPendingFiles, convId, currentUserId, isSoloConversation, isSubmitting, pendingFiles, scrollToBottom, text]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }, [handleSend]);

  const hasDraggedFiles = useCallback((e: React.DragEvent) => (
    e.dataTransfer.types.includes('Files')
  ), []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!enableDropZone || isSoloConversation) return;
    e.preventDefault();
    if (!hasDraggedFiles(e)) return;
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setDragging(true);
    }
  }, [enableDropZone, hasDraggedFiles, isSoloConversation]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!enableDropZone || isSoloConversation) return;
    e.preventDefault();
    if (!hasDraggedFiles(e)) return;
    e.dataTransfer.dropEffect = 'copy';
  }, [enableDropZone, hasDraggedFiles, isSoloConversation]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!enableDropZone || isSoloConversation) return;
    e.preventDefault();
    if (!hasDraggedFiles(e)) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  }, [enableDropZone, hasDraggedFiles, isSoloConversation]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!enableDropZone || isSoloConversation) return;
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;
    handleAddFiles(files);
  }, [enableDropZone, handleAddFiles, isSoloConversation]);

  const displayName = useMemo(
    () => (
      conversation.type === 'group'
        ? conversation.name || t('dm.groupChat')
        : Object.entries(memberNames).find(([uid]) => uid !== currentUserId)?.[1]
          || [...convMessages].reverse().reduce<string | null>((resolved, message) => {
            if (resolved) return resolved;
            const reinvitedName = typeof message.metadata?.invited_user_name === 'string'
              ? message.metadata.invited_user_name.trim()
              : '';
            if (reinvitedName) return reinvitedName;
            const leftName = typeof message.metadata?.left_user_name === 'string'
              ? message.metadata.left_user_name.trim()
              : '';
            return leftName || null;
          }, null)
          || t('dm.conversation')
    ),
    [conversation.name, conversation.type, convMessages, currentUserId, memberNames],
  );

  const displayDesc = useMemo(
    () => (
      conversation.type === 'group'
        ? t('dm.membersCount', { count: Object.keys(memberNames).length })
        : t('dm.teamMember')
    ),
    [conversation.type, memberNames],
  );

  const displayLetter = displayName.charAt(0).toUpperCase();
  const directOtherUserId = conversation.type === 'direct'
    ? Object.keys(memberNames).find((uid) => uid !== currentUserId) || null
    : null;
  const directOtherPicture = directOtherUserId
    ? (memberPictures[directOtherUserId] ?? null)
    : null;

  const sharedGroup = useMemo<ConversationSharedGroup>(() => {
    const teamName = teams.find((team) => team.team_id === conversation.team_id)?.name ?? null;
    const members = (conversation.members && conversation.members.length > 0)
      ? conversation.members.map((member) => ({
        user_id: member.user_id,
        name: member.name ?? null,
        email: member.email ?? null,
        picture: member.picture ?? null,
      }))
      : Object.keys(memberNames).map((userId) => ({
        user_id: userId,
        name: memberNames[userId] ?? null,
        email: null,
        picture: memberPictures[userId] ?? null,
      }));

    return {
      group_id: conversation.conversation_id,
      team_id: conversation.team_id,
      team_name: teamName,
      title: displayName,
      member_ids: members.map((member) => member.user_id),
      members,
      created_at: conversation.created_at,
    };
  }, [
    conversation.conversation_id,
    conversation.created_at,
    conversation.team_id,
    conversation.members,
    displayName,
    memberNames,
    memberPictures,
    teams,
  ]);

  const chatMessages = useMemo(
    () => convMessages.map((message) => toChatMessage(message, memberNames, currentUserId)),
    [convMessages, currentUserId, memberNames],
  );

  const canSend = !isSoloConversation && !isSubmitting && (text.trim().length > 0 || pendingFiles.length > 0);

  useEffect(() => {
    setHeaderAvatarError(false);
  }, [conversation.conversation_id, directOtherPicture]);

  return (
    <Container
      $dragging={effectiveDropZone && dragging}
      onDragEnter={effectiveDropZone && !isSoloConversation ? handleDragEnter : undefined}
      onDragOver={effectiveDropZone && !isSoloConversation ? handleDragOver : undefined}
      onDragLeave={effectiveDropZone && !isSoloConversation ? handleDragLeave : undefined}
      onDrop={effectiveDropZone && !isSoloConversation ? handleDrop : undefined}
    >
      <Header>
        <HeaderInner>
          <HeaderLeft>
            <HeaderAvatar>
              {conversation.type === 'direct' && directOtherPicture && !headerAvatarError ? (
                <HeaderAvatarImg
                  src={directOtherPicture}
                  alt={displayName}
                  onError={() => setHeaderAvatarError(true)}
                />
              ) : (
                displayLetter
              )}
            </HeaderAvatar>
            <HeaderInfo>
              <Name>{displayName}</Name>
              <Desc>{displayDesc}</Desc>
            </HeaderInfo>
          </HeaderLeft>
          <LeaveButton type="button" onClick={handleLeaveConversation}>
            <LogOut size={13} />
            {t('dm.leave')}
          </LeaveButton>
        </HeaderInner>
      </Header>

      <MessagesArea ref={scrollRef} onScroll={handleScroll}>
        <MessagesInner>
          {hasMore && (
            <LoadMoreBtn onClick={handleLoadMore} disabled={loading}>
              <ChevronUp size={14} />
              {loading ? t('dm.loading') : t('dm.loadPreviousMessages')}
            </LoadMoreBtn>
          )}

          {convMessages.length === 0 && !loading && (
            <EmptyState
              icon={<EmptyAvatar>{displayLetter}</EmptyAvatar>}
              description={t('dm.startConversationWith', { name: displayName })}
            />
          )}

          {chatMessages.map((message) => {
            const mentionEvent = parseMentionEvent(message.metadata);
            if (mentionEvent) {
              const isMine = message.senderId === currentUserId;
              return (
                <DMMentionNotice
                  key={message.id}
                  event={mentionEvent}
                  isMine={isMine}
                  otherName={directOtherUserId ? memberNames[directOtherUserId] : null}
                  createdAt={message.createdAt}
                />
              );
            }
            return (
              <MessageBubble
                key={message.id}
                message={message}
                sharedGroup={sharedGroup}
                currentUserId={currentUserId}
                conversationId={convId}
                onToggleReaction={toggleReaction}
                onReinviteMember={handleReinviteMember}
                showReinviteAction={canReinviteMembers && reinviteableMessageIds.has(message.id)}
                reinviting={reinvitingMessageId === message.id}
              />
            );
          })}

          {typingList.length > 0 && (
            <TypingWrap>
              {t('dm.typingIndicator', { names: typingList.map((uid) => memberNames[uid] || uid).join(', ') })}
            </TypingWrap>
          )}
        </MessagesInner>
      </MessagesArea>

      <InputWrapper>
        <InputInner>
          <OuterWrap>
            {fileAttachEnabled && (
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                multiple
                disabled={isSoloConversation}
                onChange={handleFileChange}
              />
            )}
            <InputCard $focus={focused} $transparent={transparentInput}>
              {pendingFiles.length > 0 && (
                <PendingFiles>
                  {pendingFiles.map((file) => {
                    const isImage = file.type.startsWith('image/');
                    const info = getPendingFileTypeInfo(file.name, file.type);
                    return (
                      <PendingFileCard key={file.id}>
                        {isImage && file.previewUrl ? (
                          <PendingImageThumb src={file.previewUrl} alt={file.name} />
                        ) : (
                          <PendingIconBadge $bg={info.bg} $color={info.color}>
                            {info.icon}
                            <PendingExtLabel $color={info.color}>{info.ext}</PendingExtLabel>
                          </PendingIconBadge>
                        )}
                        <PendingFileInfo>
                          <PendingFileName title={file.name}>{file.name}</PendingFileName>
                          <PendingFileSize>{formatFileSize(file.size)}</PendingFileSize>
                        </PendingFileInfo>
                        <PendingRemoveBtn
                          type="button"
                          onClick={() => handleRemovePendingFile(file.id)}
                          title={t('dm.removeAttachment')}
                        >
                          <X size={12} />
                        </PendingRemoveBtn>
                      </PendingFileCard>
                    );
                  })}
                </PendingFiles>
              )}

              <TextAreaWrap>
                <TextArea
                  ref={textRef}
                  rows={1}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPaste={handlePaste}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onKeyDown={handleKeyDown}
                  placeholder={isSoloConversation
                    ? t('dm.noMembersSendPlaceholder')
                    : t('dm.messageToTeamMember')}
                  disabled={isSoloConversation}
                />
              </TextAreaWrap>

              <InputRow>
                {fileAttachEnabled && (
                  <IconBtn
                    type="button"
                    onClick={() => !isSubmitting && !isSoloConversation && fileInputRef.current?.click()}
                    disabled={isSubmitting || isSoloConversation}
                    title={t('dm.fileUpload')}
                  >
                    <Plus size={18} />
                  </IconBtn>
                )}
                <SendBtn
                  type="button"
                  $canSend={canSend}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void handleSend()}
                  disabled={!canSend}
                  title={isSubmitting ? t('dm.sending') : t('dm.send')}
                >
                  <ArrowUp size={18} />
                </SendBtn>
              </InputRow>
            </InputCard>
          </OuterWrap>
        </InputInner>
      </InputWrapper>
      {effectiveDropZone && !isSoloConversation && dragging && <DropOverlay />}
      <Modal
        open={leaveConfirmOpen}
        onClose={() => {
          if (leavingConversation) return;
          setLeaveConfirmOpen(false);
        }}
        title={t('dm.leaveConversation')}
        width="420px"
      >
        <LeaveModalText>
          {t('dm.leaveConfirmation')}
        </LeaveModalText>
        <LeaveModalActions>
          <Button
            variant="secondary"
            onClick={() => setLeaveConfirmOpen(false)}
            disabled={leavingConversation}
          >
            {t('dm.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => void confirmLeaveConversation()}
            isLoading={leavingConversation}
          >
            {t('dm.leave')}
          </Button>
        </LeaveModalActions>
      </Modal>
    </Container>
  );
};

export default DMUserChatThread;

/* ---- Styled ---- */

const Container = styled.div<{ $dragging: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: transparent;
  position: relative;
  isolation: isolate;
  transition: background ${({ theme }) => theme.transitions.normal};

  ${({ $dragging, theme }) =>
    $dragging &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)'};
    `}
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: ${({ theme }) => theme.dm.threadMaxWidth};
  padding: ${({ theme }) => theme.dm.threadHeaderPad};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const HeaderAvatar = styled.div`
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.threadHeaderAvatarSize};
  height: ${({ theme }) => theme.dm.threadHeaderAvatarSize};
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.mode === 'dark' ? 'linear-gradient(145deg, #434345, #38383a)' : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) => theme.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-weight: 700;
  font-size: ${({ theme }) => theme.dm.threadHeaderAvatarFontSize};
  line-height: 1;
  user-select: none;
  overflow: hidden;
`;

const HeaderAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const Name = styled.div`
  font-size: ${({ theme }) => theme.dm.threadHeaderNameFontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Desc = styled.div`
  font-size: ${({ theme }) => theme.dm.threadHeaderDescFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LeaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 2px;
  }
`;

const MessagesInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.dm.threadMaxWidth};
  padding: ${({ theme }) => theme.dm.messagesPad};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.dm.messagesGap};
`;

const LoadMoreBtn = styled.button`
  align-self: center;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 16px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.dm.timeFontSize};
  cursor: pointer;
  margin-bottom: 8px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyAvatar = styled.div`
  width: ${({ theme }) => theme.dm.emptyAvatarSize};
  height: ${({ theme }) => theme.dm.emptyAvatarSize};
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.mode === 'dark' ? 'linear-gradient(145deg, #434345, #38383a)' : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) => theme.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-weight: 700;
  font-size: ${({ theme }) => theme.dm.emptyAvatarFontSize};
  user-select: none;
`;

const TypingWrap = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 4px 8px;
  font-style: italic;
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 24px;
`;

const InputInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.dm.threadMaxWidth};
`;

const OuterWrap = styled.div`
  position: relative;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const InputCard = styled.div<{ $focus: boolean; $transparent: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme, $transparent }) =>
    $transparent
      ? 'transparent'
      : theme.mode === 'dark' ? 'rgba(40,40,42,0.85)' : 'rgba(255,255,255,0.85)'};
  backdrop-filter: ${({ $transparent }) => ($transparent ? 'none' : 'blur(16px) saturate(180%)')};
  -webkit-backdrop-filter: ${({ $transparent }) => ($transparent ? 'none' : 'blur(16px) saturate(180%)')};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  border: 0.5px solid ${({ theme, $transparent }) =>
    $transparent
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'
      : theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  box-shadow: ${({ theme, $transparent }) =>
    $transparent
      ? 'none'
      : theme.mode === 'dark'
        ? '0 2px 12px rgba(0,0,0,0.25)'
        : '0 2px 12px rgba(0,0,0,0.05)'};
  transition: all 0.2s ease;
  margin: 0 16px 16px;
  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.14)'
        : 'rgba(0,0,0,0.1)'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 4px 20px rgba(0,0,0,0.35), 0 0 0 0.5px rgba(255,255,255,0.08)'
        : '0 4px 20px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)'};
    `}
`;

const PendingFiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 12px 0;
`;

const PendingFileCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
  max-width: 240px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.8)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.2)'
      : '0 1px 4px rgba(0,0,0,0.04)'};
`;

const PendingImageThumb = styled.img`
  width: ${({ theme }) => theme.input.fileIconSize};
  height: ${({ theme }) => theme.input.fileIconSize};
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const PendingIconBadge = styled.div<{ $bg: string; $color: string }>`
  width: ${({ theme }) => theme.input.fileIconSize};
  height: ${({ theme }) => theme.input.fileIconSize};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const PendingExtLabel = styled.span<{ $color: string }>`
  font-size: ${({ theme }) => theme.input.fileExtFontSize};
  font-weight: 700;
  line-height: 1;
  color: ${({ $color }) => $color};
`;

const PendingFileInfo = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PendingFileName = styled.span`
  font-size: ${({ theme }) => theme.input.fileFontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const PendingFileSize = styled.span`
  font-size: ${({ theme }) => theme.input.fileSizeFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.2;
`;

const PendingRemoveBtn = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.input.rowPad};
`;

const TextAreaWrap = styled.div`
  width: 100%;
  padding: 12px 16px 0;
`;

const TextArea = styled.textarea`
  width: 100%;
  display: block;
  box-sizing: border-box;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.input.fontSize};
  line-height: ${({ theme }) => theme.input.lineHeight};
  padding: ${({ theme }) => theme.input.textPad};
  padding-left: 8px;
  padding-right: 8px;
  min-height: 24px;
  max-height: ${({ theme }) => theme.sizes.inputMaxHeight};
  overflow-y: auto;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const IconBtn = styled.button`
  width: ${({ theme }) => theme.input.iconBtnSize};
  height: ${({ theme }) => theme.input.iconBtnSize};
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const SendBtn = styled.button<{ $canSend: boolean }>`
  width: ${({ theme }) => theme.input.iconBtnSize};
  height: ${({ theme }) => theme.input.iconBtnSize};
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};

  ${({ $canSend }) =>
    $canSend
      ? css`
          &:hover {
            opacity: 0.85;
          }
        `
      : css`
          opacity: 0.3;
          cursor: not-allowed;
        `}
`;

const LeaveModalText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.modal.bodyFontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const LeaveModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;
